import { useState, useEffect, useCallback, useMemo, type CSSProperties } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, BookOpen, Save, RefreshCw, X, GraduationCap, User, Presentation, AlertTriangle } from 'lucide-react';
import './responsavel-ficha-desktop.css';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { hasPermission } from '../../auth/permissions';
import { useCrud } from '../../api/hooks/useCrud';
import type { Turma, Materia, Professor, Aluno, HorarioAula } from '../../types/models';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';
import { resolveMateriaCorVisual } from '../../components/materias/catalogo-bncc-data';
import {
  materiaTemProfessorDisponivel,
  mergeOcupacaoComGradeLocal,
  professorOcupadoNoSlot,
  temConflitoHorario,
  type OcupacaoProfessorSlot,
} from './grade-conflito';

const DIAS = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA'] as const;
const DIA_LABELS: Record<string, string> = {
  SEGUNDA: 'SEGUNDA',
  TERCA: 'TERÇA',
  QUARTA: 'QUARTA',
  QUINTA: 'QUINTA',
  SEXTA: 'SEXTA',
  SABADO: 'SÁBADO',
};

function parseHexColor(hex?: string | null): { r: number; g: number; b: number } | null {
  if (!hex) return null;
  const raw = hex.trim().replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/** Mistura cor da matéria com branco (select legível em tamanho pequeno). */
function softMateriaBg(hex: string, whiteRatio = 0.62): string {
  const rgb = parseHexColor(hex);
  if (!rgb) return '#f8fafc';
  const mix = (c: number) => Math.round(c + (255 - c) * whiteRatio);
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
}

/** Visual alinhado ao Assistente de Grade (só UI). */
function materiaSelectStyle(corVisual?: string | null, selected?: boolean): CSSProperties {
  if (!selected || !corVisual) {
    return {
      backgroundColor: '#f8fafc',
      color: '#94a3b8',
      border: '1px solid #e2e8f0',
      fontWeight: 500,
      boxShadow: 'none',
    };
  }
  return {
    backgroundColor: softMateriaBg(corVisual),
    color: '#0f172a',
    border: `1px solid ${corVisual}`,
    boxShadow: `inset 3px 0 0 0 ${corVisual}`,
    fontWeight: 700,
  };
}

/** Remove prefixo Prof./Profª. do nome exibido na grade. */
function nomeProfessorDisplay(nome?: string | null): string {
  if (!nome) return '';
  return nome
    .replace(/^\s*prof\.?\s*/i, '')
    .replace(/^\s*profª\.?\s*/i, '')
    .replace(/^\s*professora?\s+/i, '')
    .trim();
}

/** Série à esquerda (MAIÚSCULAS) + rótulo TURMA - X à direita. */
function labelsSlotTurma(turma?: {
  nome?: string;
  serie?: string;
  sala?: string;
} | null): { serieUpper: string; turmaLabel: string } {
  const serie = (turma?.serie || '').trim();
  const nome = (turma?.nome || '').trim();
  let letra = '';
  if (serie && nome.toLowerCase().startsWith(serie.toLowerCase())) {
    letra = nome.slice(serie.length).trim().replace(/^[-–]\s*/, '');
  }
  if (!letra && turma?.sala) letra = String(turma.sala).trim();
  if (!letra && nome) {
    const parts = nome.split(/\s+/);
    letra = parts[parts.length - 1] || nome;
  }
  return {
    serieUpper: (serie || nome || '—').toUpperCase(),
    turmaLabel: `TURMA - ${(letra || 'A').toUpperCase()}`,
  };
}

interface HorarioSlot {
  id: string; turmaId: string; diaSemana: string; numeroAula: number;
  horaInicio?: string; horaFim?: string; tipo?: string; descricao?: string;
  materiaId?: string | null; professorId?: string | null;
  materia?: { id: string; nome: string; codigo?: string; corVisual?: string } | null;
  professor?: { id: string; nome: string } | null;
}

const TABS = [
  { key: 'grade', label: 'Grade de Horarios', icon: Calendar },
  { key: 'gradeProf', label: 'Grade por Professores', icon: Presentation },
  { key: 'alunos', label: 'Alunos', icon: Users },
];

export type TurmaGradeFooterChrome = {
  semSlots: boolean;
  saving: boolean;
  temErros: boolean;
  onRedefinir: () => void;
  onSalvar: () => void;
};

export function TurmaDetalhePage({
  overrideId,
  onClose,
  onEdit,
  onDeactivate,
  onGradeFooterChange,
}: {
  overrideId?: string;
  onClose?: () => void;
  onEdit?: (turma: Turma) => void;
  onDeactivate?: (turma: Turma) => void;
  /** Expõe ações da grade para o footer único do Modal (uma linha). */
  onGradeFooterChange?: (chrome: TurmaGradeFooterChrome | null) => void;
} = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = overrideId || routeId;
  const isModal = !!onClose;
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = '/' + (location.pathname.split('/')[1] || 'admin');
  const user = useAuthStore((s) => s.user);
  const escolaId = useAuthStore((s) => s.escolaId) || user?.escolaId || undefined;
  const canManage = hasPermission(user, 'turmas.manage');
  const { data: turmas } = useCrud<Turma>('turmas');
  const { data: materias } = useCrud<Materia>('materias');
  const { data: professores } = useCrud<Professor>('professores');
  const { data: alunos } = useCrud<Aluno>('alunos');
  const [tab, setTab] = useState('grade');
  const [horarios, setHorarios] = useState<HorarioSlot[]>([]);
  const [loadingGrade, setLoadingGrade] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profGradeId, setProfGradeId] = useState('');
  const [profHorarios, setProfHorarios] = useState<HorarioAula[]>([]);
  const [loadingProfGrade, setLoadingProfGrade] = useState(false);
  const [ocupacao, setOcupacao] = useState<OcupacaoProfessorSlot[]>([]);

  const turma = turmas.find((t) => t.id === id);
  const alunosTurma = alunos.filter((a) => a.turmaId === id && a.ativo);

  const reloadOcupacao = () => {
    if (!escolaId) return;
    api
      .get(ROUTES.horarios.ocupacao(escolaId))
      .then(({ data }) => setOcupacao(Array.isArray(data) ? data : []))
      .catch(() => setOcupacao([]));
  };

  // Fetch grade
  useEffect(() => {
    if (!escolaId || !id) return;
    setLoadingGrade(true);
    api.get(ROUTES.horarios.porTurma(escolaId, id)).then(({ data }) => {
      setHorarios(Array.isArray(data) ? data : []);
    }).catch(() => setHorarios([])).finally(() => setLoadingGrade(false));
    reloadOcupacao();
  }, [escolaId, id]);

  // Carga semanal do professor (leitura cross-turma)
  useEffect(() => {
    if (tab !== 'gradeProf' || !escolaId || !profGradeId) {
      if (!profGradeId) setProfHorarios([]);
      return;
    }
    let cancelled = false;
    setLoadingProfGrade(true);
    api
      .get(ROUTES.horarios.porEscola(escolaId), { params: { professorId: profGradeId } })
      .then(({ data }) => {
        if (cancelled) return;
        const list: HorarioAula[] = Array.isArray(data) ? data : (data?.data ?? []);
        setProfHorarios(list.filter((h) => h.tipo !== 'INTERVALO' && h.professorId === profGradeId));
      })
      .catch(() => {
        if (!cancelled) setProfHorarios([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProfGrade(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, escolaId, profGradeId]);

  // Criar/recriar a partir do modelo do turno (substitui "Gerar Grade" genérico)
  const redefinirGrade = useCallback(async () => {
    const temSlots = horarios.some((h) => h.tipo !== 'INTERVALO');
    const ok = confirm(
      temSlots
        ? 'Redefinir para o padrão do turno?\nMatérias e professores configurados serão perdidos.'
        : 'Criar a grade a partir do modelo do turno?',
    );
    if (!ok) return;
    try {
      const { data } = await api.post(ROUTES.horarios.redefinir(escolaId!, id!));
      setHorarios(Array.isArray(data) ? data : []);
      toast.success(temSlots ? 'Grade redefinida!' : 'Grade criada a partir do turno!');
    } catch {
      toast.error('Erro ao criar/redefinir grade do turno');
    }
  }, [horarios, escolaId, id]);

  // Atualizar slot individual
  const updateSlot = (slotId: string, field: 'materiaId' | 'professorId', value: string) => {
    setHorarios((prev) => prev.map((h) => {
      if (h.id !== slotId) return h;
      if (field === 'professorId' && value && id) {
        const turnoOpId = turma?.turnoOperacionalId ?? turma?.turnoOperacional?.id ?? null;
        const ocupacaoEfetiva = mergeOcupacaoComGradeLocal(
          ocupacao,
          prev,
          id,
          turnoOpId,
          turma?.nome,
        );
        const conflito = professorOcupadoNoSlot(ocupacaoEfetiva, {
          professorId: value,
          diaSemana: h.diaSemana,
          numeroAula: h.numeroAula,
          horaInicio: h.horaInicio,
          horaFim: h.horaFim,
          excludeSlot: { turmaId: id, diaSemana: h.diaSemana, numeroAula: h.numeroAula },
          turnoOperacionalId: turnoOpId,
        });
        if (conflito) {
          toast.error(
            `Professor ocupado em ${conflito.turmaNome || 'outra turma'}` +
              (conflito.horaInicio ? ` (${conflito.horaInicio})` : ''),
          );
          return h;
        }
      }
      const updated = { ...h, [field]: value || null };
      // Ao trocar matéria, limpar professor se ele não leciona a nova matéria
      if (field === 'materiaId') {
        const profAtual = professores.find((p) => p.id === h.professorId);
        if (profAtual && value) {
          const lecionaNovaMateria = profAtual.materias?.some((m) => m.id === value);
          if (!lecionaNovaMateria) updated.professorId = null;
        }
        if (!value) updated.professorId = null;
      }
      return updated;
    }));
  };

  // Salvar grade bulk
  const salvarGrade = useCallback(async () => {
    setSaving(true);
    try {
      const payload = horarios.map((h) => ({
        diaSemana: h.diaSemana, numeroAula: h.numeroAula,
        materiaId: h.materiaId || null, professorId: h.professorId || null,
        horaInicio: h.horaInicio || null, horaFim: h.horaFim || null,
      }));
      await api.put(ROUTES.horarios.bulk(escolaId!, id!), { horarios: payload });
      toast.success('Grade salva!');
      reloadOcupacao();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string | string[] | { message?: string } } };
      };
      const msg = ax.response?.data?.message;
      const text =
        typeof msg === 'string'
          ? msg
          : Array.isArray(msg)
            ? msg.join('; ')
            : msg && typeof msg === 'object'
              ? msg.message
              : undefined;
      toast.error(text || 'Erro ao salvar grade');
    }
    setSaving(false);
  }, [horarios, escolaId, id]);

  const gradeSemSlots = !horarios.some((h) => h.tipo !== 'INTERVALO');



  if (!turma) {
    return (
      <div className="admin-page" style={{ textAlign: 'center', padding: 60 }}>
        <p style={{ color: '#9ca3af' }}>Turma nao encontrada</p>
        <button className="btn-primary" onClick={() => navigate(`${basePath}/turmas`)}><ArrowLeft size={16} /> Voltar</button>
      </div>
    );
  }

  // Organizar grade por dia
  const horariosAula = horarios.filter((h) => h.tipo !== 'INTERVALO');
  const diasPresentes = [...new Set(horariosAula.map((h) => h.diaSemana))].sort((a, b) => DIAS.indexOf(a as any) - DIAS.indexOf(b as any));
  const aulasReaisOrdenadas = [...new Set(horariosAula.map((h) => h.numeroAula))].sort((a, b) => a - b);
  const aulasVisiveis = aulasReaisOrdenadas.slice(0, 5);
  const materiasAtivas = materias.filter((m) => m.ativo);
  const profsAtivos = professores.filter((p) => p.ativo);
  const turnoOpIdTurma = turma.turnoOperacionalId ?? turma.turnoOperacional?.id ?? null;
  // Garante turno em cada slot de ocupação (API + fallback pelas turmas carregadas)
  const ocupacaoComTurno = ocupacao.map((o) => {
    if (o.turnoOperacionalId) return o;
    const tRef = turmas.find((t) => t.id === o.turmaId);
    return {
      ...o,
      turnoOperacionalId: tRef?.turnoOperacionalId ?? tRef?.turnoOperacional?.id ?? null,
    };
  });
  const ocupacaoEfetiva = id
    ? mergeOcupacaoComGradeLocal(ocupacaoComTurno, horariosAula, id, turnoOpIdTurma, turma.nome)
    : ocupacaoComTurno;
  const capacidade = turma.capacidade ?? 0;
  const slotsPreenchidos = horariosAula.filter((h) => h.materiaId).length;
  const vagasRestantes = capacidade > 0 ? Math.max(capacidade - alunosTurma.length, 0) : 0;
  const usoPct = capacidade > 0 ? Math.round((alunosTurma.length / capacidade) * 100) : null;
  const turnoNome = turma.turnoOperacional?.nome || '—';

  const gradeTemErros = useMemo(() => {
    return horariosAula.some(h => {
      if (h.materiaId && !h.professorId) return true;
      if (h.materiaId && h.professorId && turma) {
        const toId = turma.turnoOperacionalId || turma.turnoOperacional?.id;
        const conflito = professorOcupadoNoSlot(ocupacaoEfetiva, {
          professorId: h.professorId,
          diaSemana: h.diaSemana,
          numeroAula: h.numeroAula,
          horaInicio: h.horaInicio || undefined,
          horaFim: h.horaFim || undefined,
          excludeSlot: {
            turmaId: turma.id,
            diaSemana: h.diaSemana,
            numeroAula: h.numeroAula,
          },
          turnoOperacionalId: toId,
        });
        if (conflito) return true;
      }
      return false;
    });
  }, [horariosAula, ocupacaoEfetiva, turma]);

  // Modal: ações da grade sobem para o footer único (Turmas).
  useEffect(() => {
    if (!onGradeFooterChange) return;
    if (!canManage || tab !== 'grade') {
      onGradeFooterChange(null);
      return;
    }
    onGradeFooterChange({
      semSlots: gradeSemSlots,
      saving,
      temErros: gradeTemErros,
      onRedefinir: () => void redefinirGrade(),
      onSalvar: () => void salvarGrade(),
    });
    return () => onGradeFooterChange(null);
  }, [
    onGradeFooterChange,
    canManage,
    tab,
    gradeSemSlots,
    gradeTemErros,
    saving,
    redefinirGrade,
    salvarGrade,
  ]);

  const gradeFooter =
    !isModal && canManage ? (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <button
          type="button"
          className={gradeSemSlots ? 'btn-primary' : undefined}
          style={
            gradeSemSlots
              ? { fontSize: '0.72rem', height: 28, padding: '0 8px' }
              : {
                  background: '#fff',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  padding: '0 8px',
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  height: 28,
                  color: '#334155',
                }
          }
          onClick={() => void redefinirGrade()}
        >
          {gradeSemSlots ? (
            <>
              <Calendar size={11} /> Criar Grade
            </>
          ) : (
            <>
              <RefreshCw size={11} /> Redefinir Turno
            </>
          )}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void salvarGrade()}
          disabled={saving || gradeSemSlots || gradeTemErros}
          style={{ fontSize: '0.72rem', height: 28, padding: '0 8px', opacity: (saving || gradeSemSlots || gradeTemErros) ? 0.6 : 1, cursor: (saving || gradeSemSlots || gradeTemErros) ? 'not-allowed' : 'pointer' }}
        >
          <Save size={11} /> {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    ) : null;

  const gradeBody = loadingGrade ? (
    <p style={{ textAlign: 'center', color: '#9ca3af', padding: 34 }}>Carregando grade...</p>
  ) : horarios.length === 0 ? (
    <div style={{ textAlign: 'center', padding: 48, background: '#f9fafb', borderRadius: 12, border: '1px dashed #d1d5db' }}>
      <Calendar size={36} style={{ color: '#d1d5db', margin: '0 auto 14px' }} />
      <p style={{ color: '#9ca3af', fontWeight: 500 }}>Nenhum horário registrado</p>
      <p style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>
        Use &quot;Criar Grade&quot; no rodapé para gerar os slots a partir do turno
      </p>
    </div>
  ) : (
    <div className="turma-ficha-grade-wrap" style={{ overflow: 'auto', maxHeight: '100%' }}>
      <div className="dt-container" style={{ overflow: 'auto' }}>
        <table className="dt-table turma-ficha-grade-table" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead>
            <tr>
              <th className="turma-ficha-grade-th-meta" style={{ position: 'sticky', top: 0, left: 0, zIndex: 6, background: 'var(--bg-card, #f8fafc)', width: 75, minWidth: 75 }}>AULA</th>
              <th className="turma-ficha-grade-th-meta" style={{ position: 'sticky', top: 0, left: 75, zIndex: 6, background: 'var(--bg-card, #f8fafc)', width: 70, minWidth: 70, borderRight: '2px solid var(--border-color, #e2e8f0)' }}>HORÁRIO</th>
              {diasPresentes.map((d) => (
                <th key={d} style={{ position: 'sticky', top: 0, zIndex: 4, background: 'var(--bg-card, #f8fafc)', minWidth: 140 }}>{DIA_LABELS[d] || d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aulasVisiveis.map((numeroAulaReal, idx) => {
              const numeroVisual = idx + 1;
              const sampleSlot = horariosAula.find((h) => h.numeroAula === numeroAulaReal);
              const descricaoAula =
                sampleSlot?.descricao?.trim() ||
                `${numeroVisual}ª Aula`;
              return (
                <tr key={numeroAulaReal} className="dt-row" style={{ background: numeroVisual % 2 === 0 ? '#f8fafc' : 'white' }}>
                  <td className="turma-ficha-grade-meta-aula" style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg-card, #f8fafc)', width: 75, minWidth: 75 }}>
                    {descricaoAula}
                  </td>
                  <td className="turma-ficha-grade-meta-hora" style={{ position: 'sticky', left: 75, zIndex: 2, background: 'var(--bg-card, #f8fafc)', width: 70, minWidth: 70, borderRight: '2px solid var(--border-color, #e2e8f0)' }}>
                    {sampleSlot?.horaInicio && sampleSlot?.horaFim ? (
                      <div className="turma-ficha-grade-hora-stack">
                        <span>{sampleSlot.horaInicio}</span>
                        <span>{sampleSlot.horaFim}</span>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  {diasPresentes.map((dia) => {
                    const slot = horariosAula.find((h) => h.diaSemana === dia && h.numeroAula === numeroAulaReal);
                    if (!slot) return <td key={dia} className="turma-ficha-grade-cell" style={{ background: '#f8fafc', textAlign: 'center', color: '#94a3b8' }}>-</td>;
                    const materiaSelecionada = !!slot.materiaId;
                    const professorSelecionado = !!slot.professorId;
                    const excludeSlot = {
                      turmaId: id!,
                      diaSemana: slot.diaSemana,
                      numeroAula: slot.numeroAula,
                    };
                    const materiasDisponiveis = materiasAtivas.filter((m) => {
                      if (m.id === slot.materiaId) return true;
                      return materiaTemProfessorDisponivel(m.id, profsAtivos, ocupacaoEfetiva, {
                        diaSemana: slot.diaSemana,
                        numeroAula: slot.numeroAula,
                        horaInicio: slot.horaInicio,
                        horaFim: slot.horaFim,
                        excludeSlot,
                        turnoOperacionalId: turnoOpIdTurma,
                        slotProfessorId: slot.professorId,
                      });
                    });
                    const materiaRef =
                      materiasDisponiveis.find((m) => m.id === slot.materiaId) ||
                      materiasAtivas.find((m) => m.id === slot.materiaId);
                    const materiaCor = materiaSelecionada
                      ? resolveMateriaCorVisual({
                          corVisual: materiaRef?.corVisual ?? (slot.materia as { corVisual?: string } | null)?.corVisual,
                          nome: materiaRef?.nome ?? slot.materia?.nome,
                          codigo: materiaRef?.codigo ?? slot.materia?.codigo,
                        })
                      : null;
                    return (
                      <td key={dia} className="turma-ficha-grade-cell">
                        <div className="turma-ficha-grade-slot">
                          <select
                            className="turma-ficha-grade-materia"
                            title={
                              materiaRef?.nome
                                ? materiaRef.nome
                                : 'Só matérias com professor livre neste horário'
                            }
                            style={materiaSelectStyle(materiaCor, materiaSelecionada)}
                            value={slot.materiaId || ''}
                            onChange={(e) => updateSlot(slot.id, 'materiaId', e.target.value)}
                            disabled={!canManage}
                          >
                            <option value="">Matéria...</option>
                            {materiasDisponiveis.map((m) => (
                              <option key={m.id} value={m.id}>{m.nome}</option>
                            ))}
                          </select>
                          <select
                            className="turma-ficha-grade-professor"
                            style={{
                              background: professorSelecionado ? '#fff' : '#f8fafc',
                              color: professorSelecionado ? '#0f172a' : '#94a3b8',
                              fontWeight: professorSelecionado ? 600 : 400,
                              opacity: materiaSelecionada ? 1 : 0.55,
                              cursor: materiaSelecionada ? 'pointer' : 'not-allowed',
                            }}
                            value={slot.professorId || ''}
                            onChange={(e) => updateSlot(slot.id, 'professorId', e.target.value)}
                            disabled={!materiaSelecionada || !canManage}
                          >
                            <option value="">Professor...</option>
                            {(materiaSelecionada
                              ? profsAtivos.filter((p) => {
                                  // Sempre manter o professor já salvo no slot (mesmo sem vínculo matéria)
                                  if (p.id === slot.professorId) return true;
                                  if (!p.materias?.some((m) => m.id === slot.materiaId)) return false;
                                  return !professorOcupadoNoSlot(ocupacaoEfetiva, {
                                    professorId: p.id,
                                    diaSemana: slot.diaSemana,
                                    numeroAula: slot.numeroAula,
                                    horaInicio: slot.horaInicio,
                                    horaFim: slot.horaFim,
                                    excludeSlot,
                                    turnoOperacionalId: turnoOpIdTurma,
                                  });
                                })
                              : []
                            ).map((p) => (
                              <option key={p.id} value={p.id}>{nomeProfessorDisplay(p.nome)}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const profIdsNaTurma = new Set(
    horariosAula.map((h) => h.professorId).filter((pid): pid is string => !!pid),
  );
  const profsParaPicker = [...profsAtivos].sort((a, b) => {
    const aIn = profIdsNaTurma.has(a.id) ? 0 : 1;
    const bIn = profIdsNaTurma.has(b.id) ? 0 : 1;
    if (aIn !== bIn) return aIn - bIn;
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });
  const profsNestaTurma = profsParaPicker.filter((p) => profIdsNaTurma.has(p.id));
  const profsOutrasTurmas = profsParaPicker.filter((p) => !profIdsNaTurma.has(p.id));

  const profAulas = profHorarios.filter((h) => h.tipo !== 'INTERVALO');
  const profAulasNums = [...new Set([
    ...aulasVisiveis,
    ...profAulas.map((h) => h.numeroAula),
  ])].sort((a, b) => a - b).slice(0, 6);
  const profDias = DIAS.filter((d) =>
    diasPresentes.includes(d) || profAulas.some((h) => h.diaSemana === d),
  );
  const diasGradeProf = profDias.length > 0 ? profDias : [...DIAS];
  const totalCargaProf = profAulas.length;
  const cargaNestaTurma = profAulas.filter((h) => h.turmaId === id).length;

  const gradeProfTab = (
    <div className={isModal ? 'turma-ficha-panel-scroll' : undefined} style={isModal ? undefined : { padding: 12, overflowY: 'auto', flex: 1 }}>
      <div className="turma-ficha-grade-prof-toolbar">
        <label className="turma-ficha-grade-prof-label">
          <span>Professor</span>
          <select
            className="turma-ficha-grade-prof-select"
            value={profGradeId}
            onChange={(e) => setProfGradeId(e.target.value)}
          >
            <option value="">Selecione um professor…</option>
            {profsNestaTurma.length > 0 && (
              <optgroup label="NESTA TURMA">
                {profsNestaTurma.map((p) => (
                  <option key={p.id} value={p.id}>{nomeProfessorDisplay(p.nome)}</option>
                ))}
              </optgroup>
            )}
            {profsOutrasTurmas.length > 0 && (
              <optgroup label="OUTRAS TURMAS">
                {profsOutrasTurmas.map((p) => (
                  <option key={p.id} value={p.id}>{nomeProfessorDisplay(p.nome)}</option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
        {profGradeId && (
          <p className="turma-ficha-grade-prof-meta">
            {totalCargaProf} aula(s) na semana · {cargaNestaTurma} nesta turma
            <span className="turma-ficha-grade-prof-hint">
              Carga da escola (todos os turnos) — conflito só se horários se sobrepõem. Edite na Grade de Horários.
            </span>
          </p>
        )}
      </div>

      {!profGradeId ? (
        <div className="turma-ficha-grade-prof-empty">
          <Presentation size={36} />
          <p>Escolha um professor para ver a carga semanal</p>
          <p className="turma-ficha-grade-prof-empty-sub">Slots desta turma ficam em destaque; outras turmas aparecem em tom secundário.</p>
        </div>
      ) : loadingProfGrade ? (
        <p style={{ textAlign: 'center', color: '#9ca3af', padding: 34 }}>Carregando carga do professor…</p>
      ) : (
        <div className="turma-ficha-grade-wrap">
          <div className="dt-container">
            <table className="dt-table turma-ficha-grade-table turma-ficha-grade-prof-table">
              <thead>
                <tr>
                  <th style={{ padding: '6px 4px' }}>#</th>
                  {diasGradeProf.map((d) => (
                    <th key={d}>{DIA_LABELS[d] || d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(profAulasNums.length > 0 ? profAulasNums : [1, 2, 3, 4, 5]).map((numeroAula, idx) => (
                  <tr key={numeroAula} className="dt-row" style={{ background: (idx + 1) % 2 === 0 ? '#f8fafc' : 'white' }}>
                    <td style={{ fontWeight: 700, color: '#64748b', textAlign: 'center', padding: '4px 3px', borderRight: '1px solid #e2e8f0' }}>
                      {numeroAula}a
                    </td>
                    {diasGradeProf.map((dia) => {
                      const slots = profAulas
                        .filter((h) => h.diaSemana === dia && h.numeroAula === numeroAula)
                        .sort((a, b) => (a.horaInicio || '').localeCompare(b.horaInicio || ''));
                      if (slots.length === 0) {
                        return (
                          <td key={dia} className="turma-ficha-grade-cell turma-ficha-grade-prof-cell--livre">
                            <div className="turma-ficha-grade-prof-slot turma-ficha-grade-prof-slot--livre">Livre</div>
                          </td>
                        );
                      }
                      // Mesmo nº de aula em Manhã/Tarde/Noite NÃO é conflito se o relógio não se sobrepõe
                      const conflito = temConflitoHorario(slots);
                      return (
                        <td key={dia} className="turma-ficha-grade-cell">
                          <div className={`turma-ficha-grade-prof-stack${conflito ? ' turma-ficha-grade-prof-stack--conflito' : ''}`}>
                            {conflito && (
                              <div className="turma-ficha-grade-prof-warn" title="Horários se sobrepõem">
                                <AlertTriangle size={11} aria-hidden />
                                <span>Conflito</span>
                              </div>
                            )}
                            {slots.map((slot) => {
                              const destaTurma = slot.turmaId === id;
                              const materiaNome =
                                slot.materia?.nome ||
                                materiasAtivas.find((m) => m.id === slot.materiaId)?.nome ||
                                slot.materia?.codigo ||
                                '—';
                              const cor = resolveMateriaCorVisual({
                                corVisual: slot.materia?.corVisual,
                                nome: materiaNome,
                                codigo: slot.materia?.codigo,
                              });
                              const { serieUpper, turmaLabel } = labelsSlotTurma(slot.turma);
                              const turnoLabel =
                                slot.turma?.turnoOperacional?.nome ||
                                (slot.turma as { turno?: string } | undefined)?.turno ||
                                '';
                              return (
                                <div
                                  key={slot.id}
                                  className={`turma-ficha-grade-prof-slot${destaTurma ? ' turma-ficha-grade-prof-slot--atual' : ' turma-ficha-grade-prof-slot--outra'}`}
                                  style={{
                                    borderColor: cor,
                                    background: softMateriaBg(cor, destaTurma ? 0.78 : 0.9),
                                    boxShadow: destaTurma ? `inset 3px 0 0 0 ${cor}` : undefined,
                                  }}
                                  title={[
                                    slot.turma?.nome || 'Turma',
                                    turnoLabel,
                                    materiaNome,
                                    slot.horaInicio && slot.horaFim ? `${slot.horaInicio}–${slot.horaFim}` : '',
                                    conflito ? 'Conflito: horários se sobrepõem' : '',
                                  ].filter(Boolean).join(' · ')}
                                >
                                  <div className="turma-ficha-grade-prof-head">
                                    <span className="turma-ficha-grade-prof-serie">{serieUpper}</span>
                                    <span className="turma-ficha-grade-prof-turma-tag">{turmaLabel}</span>
                                  </div>
                                  <span className="turma-ficha-grade-prof-materia">
                                    {materiaNome}
                                    {slot.horaInicio ? ` · ${slot.horaInicio}` : ''}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalCargaProf === 0 && (
            <p className="turma-ficha-grade-prof-empty-sub" style={{ marginTop: 12, textAlign: 'center' }}>
              Este professor ainda não tem aulas na grade da escola.
            </p>
          )}
        </div>
      )}
    </div>
  );

  const alunosTab = (
    <div className={isModal ? 'turma-ficha-panel-scroll' : undefined} style={isModal ? undefined : { padding: 12, overflowY: 'auto', flex: 1 }}>
      <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 10px' }}>{alunosTurma.length} aluno(s) vinculado(s)</p>
      {alunosTurma.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 46, background: '#f9fafb', borderRadius: 12, border: '1px dashed #d1d5db' }}>
          <Users size={34} style={{ color: '#d1d5db', margin: '0 auto 14px' }} />
          <p style={{ color: '#9ca3af' }}>Nenhum aluno vinculado a esta turma</p>
        </div>
      ) : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <div className="dt-container">
            <table className="dt-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Matrícula</th>
                  <th>Nome</th>
                  <th>Gênero</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {alunosTurma.map((a, i) => (
                  <tr key={a.id} className="dt-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/alunos/${a.id}`)}>
                    <td style={{ color: '#9ca3af' }}>{i + 1}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{a.matricula}</td>
                    <td style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 500 }}>
                      {a.fotoUrl ? (
                        <img src={a.fotoUrl} alt={a.nome} style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                          <User size={14} />
                        </div>
                      )}
                      {a.nome}
                    </td>
                    <td>{a.genero === 'MASCULINO' ? 'M' : a.genero === 'FEMININO' ? 'F' : '-'}</td>
                    <td><span className="badge badge-active">Ativo</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const tabNav = (
    <nav className={isModal ? 'resp-ficha-tabs resp-ficha-tabs--scroll' : undefined} style={isModal ? undefined : { display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', padding: '0 10px', background: '#f8fafc' }} aria-label="Secções">
      {TABS.map((t) =>
        isModal ? (
          <button
            key={t.key}
            type="button"
            className={`resp-ficha-tab${tab === t.key ? ' resp-ficha-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ) : (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '11px 12px',
              fontSize: '0.8rem',
              fontWeight: tab === t.key ? 700 : 600,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: tab === t.key ? '2px solid #2563eb' : '2px solid transparent',
              color: tab === t.key ? '#2563eb' : '#64748b',
              marginBottom: -1,
            }}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ),
      )}
    </nav>
  );

  if (isModal) {
    return (
      <div className="resp-ficha">
        <header className="resp-ficha-head" style={{ padding: '8px 12px', gap: 8, alignItems: 'center' }}>
          <span className="resp-ficha-avatar-ph" style={{ background: '#dbeafe', color: '#1e40af', width: 36, height: 36, minWidth: 36, borderRadius: 8 }}>
            <GraduationCap size={18} />
          </span>
          <div className="resp-ficha-head-main" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <h2 className="resp-ficha-name" style={{ fontSize: '0.92rem', lineHeight: 1.1 }}>{turma.nome}</h2>
              <span className={`resp-ficha-badge ${turma.ativo ? 'resp-ficha-badge--ok' : 'resp-ficha-badge--off'}`} style={{ padding: '1px 6px', fontSize: '0.62rem' }}>
                {turma.ativo ? 'Ativa' : 'Inativa'}
              </span>
            </div>
            <div className="resp-ficha-meta-line" style={{ marginTop: 2, gap: 4 }}>
              <span className="resp-ficha-sub" style={{ fontSize: '0.68rem' }}>
                {turma.serie} · {turnoNome} · {turma.anoLetivo}
                {turma.sala ? ` · Sala ${turma.sala}` : ''}
              </span>
            </div>
          </div>
          {/* KPIs no header para ganhar espaço vertical */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 52 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>
                {alunosTurma.length}
                {capacidade > 0 ? `/${capacidade}` : ''}
              </span>
              <span style={{ fontSize: '0.52rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Alunos</span>
            </div>
            
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 52 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>
                {slotsPreenchidos}/{horariosAula.length}
              </span>
              <span style={{ fontSize: '0.52rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Slots</span>
            </div>
          </div>

          <button type="button" className="resp-ficha-close" onClick={onClose} aria-label="Fechar" style={{ width: 28, height: 28 }}>
            <X size={15} />
          </button>
        </header>
        <div className="resp-ficha-body">
          <div className="resp-ficha-stack turma-ficha-stack">
            {tabNav}
            <div className="resp-ficha-panel turma-ficha-panel">
              {tab === 'grade' && (
                <div className="turma-ficha-panel-scroll">
                  {gradeBody}
                </div>
              )}
              {tab === 'gradeProf' && gradeProfTab}
              {tab === 'alunos' && alunosTab}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" style={{ marginTop: -10, position: 'relative' }}>
      <button
        className="action-btn"
        onClick={() => navigate(`${basePath}/turmas`)}
        title="Voltar para turmas"
        style={{
          position: 'fixed',
          right: 18,
          bottom: 18,
          zIndex: 6,
          width: 38,
          height: 38,
          borderRadius: 999,
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.22)',
          background: '#1e40af',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.45)',
        }}
      >
        <ArrowLeft size={18} />
      </button>

      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          width: '100%',
          borderRadius: 12,
          border: '1px solid #1d4ed8',
          background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)',
          color: 'white',
          padding: '8px 9px',
          marginBottom: 10,
          boxShadow: '0 10px 30px rgba(37, 99, 235, 0.25)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(255,255,255,0.18)', display: 'grid', placeItems: 'center' }}>
            <BookOpen size={13} />
          </div>
          <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 800, lineHeight: 1 }}>{turma.nome}</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
          <div style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 9, padding: '6px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', opacity: 0.9 }}>
              <BookOpen size={11} /> Contexto
            </div>
            <div style={{ marginTop: 3, fontSize: '0.74rem', fontWeight: 700, lineHeight: 1.15 }}>
              {turma.nome} - {turma.serie}
            </div>
            <div style={{ marginTop: 2, fontSize: '0.66rem', opacity: 0.95, lineHeight: 1.15 }}>
              Turno {turnoNome} | Ano {turma.anoLetivo} | Sala {turma.sala || '—'}
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 9, padding: '6px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', opacity: 0.9 }}>
              <Users size={11} /> Alunos
            </div>
            <div style={{ marginTop: 3, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 800, lineHeight: 1.1 }}>
                {alunosTurma.length} / {capacidade > 0 ? capacidade : '—'}
              </div>
              <div style={{ fontSize: '0.66rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                Uso {usoPct != null ? `${usoPct}%` : '—'} | Vagas {capacidade > 0 ? vagasRestantes : '—'}
              </div>
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 9, padding: '6px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', opacity: 0.9 }}>
              <Calendar size={11} /> Grade
            </div>
            <div style={{ marginTop: 3, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 800, lineHeight: 1.1 }}>{horariosAula.length} slots</div>
              <div style={{ fontSize: '0.66rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                {slotsPreenchidos} preenchidos | {Math.max(horariosAula.length - slotsPreenchidos, 0)} livres
              </div>
            </div>
          </div>
        </div>
      </div>

      <section
        style={{
          background: 'var(--bg-card, white)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 14,
          overflow: 'hidden',
          minWidth: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {tabNav}
        {tab === 'grade' && (
          <div style={{ padding: 12, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{gradeBody}</div>
            {gradeFooter}
          </div>
        )}
        {tab === 'gradeProf' && gradeProfTab}
        {tab === 'alunos' && alunosTab}
      </section>
    </div>
  );
}
